import React from 'react';
import PropTypes from 'prop-types';
import './RadioButton.css';

/**
 * RadioButton — Design System
 *
 * Single selectable radio option with a circular selector, a title and an
 * optional description. Wraps a native `<input type="radio">` (visually
 * hidden) so it inherits keyboard/group semantics and works inside forms.
 *
 * Two sizes (separate Figma components):
 *   - large  → 32px selector, title `typo-h4`, description `typo-body`.
 *              Selected fill = brand success (green).   Figma 3804:18116
 *   - medium → 24px selector, title `typo-body`, description `typo-body-sm`.
 *              Selected fill = gray-10 (black).          Figma 4033:280
 *
 * Visual state derives from the native input's `checked`/`:checked`, mirroring
 * the existing `:has(input:checked)` pattern used elsewhere in the codebase.
 */
const RadioButton = ({
  size = 'medium',
  title,
  description = null,
  checked = false,
  disabled = false,
  name,
  value,
  onChange,
  className = '',
  ...props
}) => {
  const titleClass = size === 'large' ? 'typo-h4' : 'typo-body';
  const descriptionClass = size === 'large' ? 'typo-body' : 'typo-body-sm';

  const rootClass = [
    'ds-radio',
    `ds-radio--${size}`,
    disabled ? 'ds-radio--disabled' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <label className={rootClass}>
      <input
        type="radio"
        className="ds-radio__input"
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        {...props}
      />
      <span className="ds-radio__main">
        <span className="ds-radio__selector" aria-hidden="true">
          <span className="ds-radio__center" />
        </span>
        {title != null && (
          <span className={`ds-radio__title ${titleClass}`}>{title}</span>
        )}
      </span>
      {description != null && (
        <span className={`ds-radio__description ${descriptionClass}`}>
          {description}
        </span>
      )}
    </label>
  );
};

RadioButton.propTypes = {
  size: PropTypes.oneOf(['large', 'medium']),
  title: PropTypes.node,
  description: PropTypes.node,
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  name: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  className: PropTypes.string,
};

export default RadioButton;
